import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useSite, siteText } from "@/context/SiteContext";
import { toStreamUrl } from "@/lib/driveUrl";
import { DANCES } from "@/constants/dances";
import DanceStage from "@/components/PiperDancer";
import Seo from "@/components/Seo";

/** Petites barres d'égaliseur animées pendant la lecture */
function Equalizer() {
  return (
    <div className="flex h-5 items-end justify-center gap-1" aria-hidden="true">
      {[0.9, 0.55, 1, 0.7, 0.8].map((d, i) => (
        <span
          key={i}
          className="eq-bar w-1 rounded-full bg-[#B83227]"
          style={{ height: `${d * 20}px`, animationDelay: `${i * 140}ms`, animationDuration: `${0.7 + d * 0.5}s` }}
        />
      ))}
    </div>
  );
}

export default function Audio() {
  const [tracks, setTracks] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const audioRefs = useRef({});
  const { settings } = useSite();

  useEffect(() => {
    api
      .get("/media")
      .then((r) => setTracks(r.data.filter((m) => m.kind === "audio")))
      .catch(() => setTracks([]));
  }, []);

  const togglePlay = (track) => {
    const el = audioRefs.current[track.id];
    if (!el) return;
    if (playingId === track.id) {
      el.pause();
      setPlayingId(null);
    } else {
      if (playingId && audioRefs.current[playingId]) audioRefs.current[playingId].pause();
      el.currentTime = 0;
      el.play().catch(() => setPlayingId(null));
      setPlayingId(track.id);
    }
  };

  return (
    <div data-testid="audio-page">
      <Seo title="Enregistrements audio" description="Écoutez les enregistrements des Sonneurs de la Côte : airs de cornemuse du Centre-France et répertoire de bal trad." path="/audio" />
      <section className="relative overflow-hidden border-b border-stone-800 bg-[#1E1B18] py-16 text-white">
        <div aria-hidden="true" className="pointer-events-none absolute right-4 top-1/2 hidden h-44 w-44 -translate-y-1/2 lg:block">
          <div className="rounde-slow relative h-full w-full">
            {Array.from({ length: 16 }).map((_, i) => (
              <span key={i} className="absolute left-1/2 top-1/2 h-2 w-2 rounded-full bg-amber-200/50" style={{ transform: `rotate(${i * 22.5}deg) translate(82px)` }} />
            ))}
          </div>
        </div>
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
          <span className="text-xs font-bold uppercase tracking-widest text-amber-200">{siteText(settings, "page_audio_label", "Écouter")}</span>
          <h1 className="mt-2 font-display text-4xl font-bold uppercase tracking-tight sm:text-6xl">{siteText(settings, "page_audio_title", "Nos enregistrements")}</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-stone-300 sm:text-base">
            {siteText(settings, "page_audio_intro", "Quelques airs de notre répertoire, enregistrés au fil des bals et des répétitions — montez le son !")}
          </p>
        </div>
      </section>

      <section className="bg-[#FAF6F0] py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          {tracks === null ? (
            <p className="py-10 text-sm text-stone-500">Chargement...</p>
          ) : tracks.length === 0 ? (
            <p className="my-10 rounded-xl border border-dashed border-stone-300 bg-white p-8 text-center text-sm text-stone-500">
              Aucun enregistrement pour le moment — revenez bientôt.
            </p>
          ) : (
            <ul className="space-y-4">
              {tracks.map((t, i) => {
                const playing = playingId === t.id;
                const d = DANCES[t.dance] || DANCES[""];
                return (
                  <li
                    key={t.id}
                    data-testid={`audio-track-${i}`}
                    className={`flex items-center gap-4 rounded-2xl border p-4 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md sm:gap-6 sm:p-5 ${
                      playing ? "border-[#B83227]/40 bg-[#FDF8F2]" : "border-stone-200 bg-white"
                    }`}
                  >
                    <DanceStage dance={t.dance} playing={playing} onToggle={() => togglePlay(t)} title={t.title} testId={`audio-play-${i}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                        <h3 data-testid={`audio-title-${i}`} className={`font-display text-lg font-bold uppercase leading-tight transition-colors duration-300 sm:text-xl ${playing ? "text-[#B83227]" : "text-[#1E1B18]"}`}>
                          {t.title}
                        </h3>
                        {t.dance && DANCES[t.dance] && (
                          <span data-testid={`audio-dance-${i}`} className="rounded-full bg-[#B83227]/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#B83227]">
                            {d.label}
                          </span>
                        )}
                      </div>
                      {t.description && <p className="mt-1 text-xs leading-relaxed text-stone-500">{t.description}</p>}
                    </div>
                    <div className="hidden w-14 sm:block">{playing && <Equalizer />}</div>
                    <audio
                      ref={(el) => (audioRefs.current[t.id] = el)}
                      src={toStreamUrl(t.url)}
                      preload="none"
                      onEnded={() => setPlayingId(null)}
                    />
                  </li>
                );
              })}
            </ul>
          )}
          <p className="mt-10 text-center text-xs leading-relaxed text-stone-500">
            {siteText(settings, "audio_hint", "Cliquez sur la petite scène pour lancer un air : les danseurs exécutent la danse du morceau tant que la musique joue. Recliquez pour arrêter.")}
          </p>
        </div>
      </section>
    </div>
  );
}
